import { supabase } from "@/integrations/supabase/client";

export interface TestResult {
  id: string;
  testName: string; // Primary field name for consistency
  name?: string; // For backward compatibility
  status: "success" | "failure" | "running" | "passed" | "failed" | "pending";
  startTime: Date;
  endTime?: Date;
  duration?: number;
  details?: string;
  error?: string;
  errorMessage?: string; // For TestingComponent compatibility
  debugData?: any; // For TestingComponent compatibility
  category: string;
}

export interface TestLog {
  id: string;
  timestamp: Date;
  level: "info" | "warn" | "error" | "success";
  message: string;
  details?: any;
  category: string;
}

export interface DatabaseState {
  tableName: string;
  rowCount: number;
  sampleData?: any[];
  error?: string;
}

export interface EdgeFunctionResult {
  functionName: string;
  success: boolean;
  responseTime: number;
  statusCode?: number;
  response?: any;
  error?: string;
}

/**
 * Testing utilities for Cali.AI end-to-end testing
 * Provides comprehensive testing infrastructure with debugging capabilities
 */
export class TestingUtils {
  /**
   * Execute a test with proper timing and error handling
   */
  static async executeTest<T>(
    testName: string,
    testFn: () => Promise<T>,
    onLog?: (log: TestLog) => void,
    category: string = "general"
  ): Promise<TestResult> {
    const testId = `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const startTime = new Date();
    
    const log = (level: TestLog["level"], message: string, details?: any) => {
      console.log(`[${category.toUpperCase()}] ${level.toUpperCase()}: ${message}`, details);
      if (onLog) {
        onLog({
          id: `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          timestamp: new Date(),
          level,
          message,
          details,
          category
        });
      }
    };

    log("info", `Starting test: ${testName}`);

    try {
      const result = await testFn();
      const endTime = new Date();
      const duration = endTime.getTime() - startTime.getTime();

      log("success", `Test completed successfully: ${testName}`, result);

      return {
        id: testId,
        testName: testName,
        name: testName, // For backward compatibility
        status: "success",
        startTime,
        endTime,
        duration,
        details: typeof result === "object" ? JSON.stringify(result, null, 2) : String(result),
        category
      };
    } catch (error) {
      const endTime = new Date();
      const duration = endTime.getTime() - startTime.getTime();
      const errorMessage = error instanceof Error ? error.message : String(error);

      log("error", `Test failed: ${testName}`, { error: errorMessage, stack: error instanceof Error ? error.stack : undefined });

      return {
        id: testId,
        testName: testName,
        name: testName, // For backward compatibility
        status: "failure",
        startTime,
        endTime,
        duration,
        error: errorMessage,
        category
      };
    }
  }

  /**
   * Get database state for all Cali.AI tables
   */
  static async getDatabaseState(): Promise<DatabaseState[]> {
    const calieTablesList = [
      'calie_tenants',
      'calie_users', 
      'calie_onboarding_state',
      'calie_numbers',
      'calie_campaigns',
      'calie_calls'
    ] as const;

    const results: DatabaseState[] = [];

    for (const tableName of calieTablesList) {
      try {
        const { data, error, count } = await supabase
          .from(tableName)
          .select('*', { count: 'exact' })
          .limit(5);

        if (error) {
          results.push({
            tableName,
            rowCount: 0,
            error: error.message
          });
        } else {
          results.push({
            tableName,
            rowCount: count || 0,
            sampleData: data
          });
        }
      } catch (err) {
        results.push({
          tableName,
          rowCount: 0,
          error: err instanceof Error ? err.message : String(err)
        });
      }
    }

    return results;
  }

  /**
   * Test edge function with performance monitoring
   */
  static async testEdgeFunction(
    functionName: string,
    payload?: any
  ): Promise<EdgeFunctionResult> {
    const startTime = performance.now();

    try {
      const { data, error } = await supabase.functions.invoke(functionName, {
        body: payload || {}
      });

      const endTime = performance.now();
      const responseTime = endTime - startTime;

      if (error) {
        return {
          functionName,
          success: false,
          responseTime,
          error: error.message
        };
      }

      return {
        functionName,
        success: true,
        responseTime,
        statusCode: 200,
        response: data
      };
    } catch (err) {
      const endTime = performance.now();
      const responseTime = endTime - startTime;

      return {
        functionName,
        success: false,
        responseTime,
        error: err instanceof Error ? err.message : String(err)
      };
    }
  }

  /**
   * Validate RLS policy enforcement
   */
  static async validateRLSPolicies(): Promise<{ table: string; hasRLS: boolean; policyCount: number }[]> {
    try {
      const { data, error } = await supabase
        .rpc('exec_sql', {
          sql: `
            SELECT 
              schemaname,
              tablename,
              rowsecurity as has_rls,
              (
                SELECT COUNT(*)
                FROM pg_policies p
                WHERE p.schemaname = t.schemaname 
                AND p.tablename = t.tablename
              ) as policy_count
            FROM pg_tables t
            WHERE schemaname = 'public'
            AND tablename LIKE 'calie_%'
            ORDER BY tablename;
          `
        });

      if (error) {
        throw error;
      }

      return data || [];
    } catch (err) {
      console.error('Error validating RLS policies:', err);
      return [];
    }
  }

  /**
   * Clean up test environment with comprehensive reset
   */
  static async cleanupTestEnvironment(): Promise<void> {
    try {
      console.log('[TESTING] Starting test environment cleanup...');
      
      // Reset onboarding state for current tenant
      const { error: resetError } = await supabase.functions.invoke('Calie_reset_onboarding');
      if (resetError) {
        console.error('[TESTING] Failed to reset onboarding:', resetError);
        throw new Error(`Failed to reset onboarding: ${resetError.message}`);
      }

      console.log('[TESTING] Test environment cleanup completed successfully');
    } catch (error) {
      console.error('[TESTING] Cleanup failed:', error);
      throw error;
    }
  }

  /**
   * Validate session and authentication state
   */
  static async validateAuthState(): Promise<{
    isAuthenticated: boolean;
    userId?: string;
    tenantId?: string;
    sessionValid: boolean;
    tokenExpiry?: string;
  }> {
    try {
      const { data: session, error } = await supabase.auth.getSession();
      
      if (error || !session?.session) {
        return {
          isAuthenticated: false,
          sessionValid: false
        };
      }

      const user = session.session.user;
      const expiresAt = new Date(session.session.expires_at! * 1000);

      // Get tenant info
      const { data: userData } = await supabase
        .from('calie_users')
        .select('active_tenant_id')
        .eq('user_id', user.id)
        .maybeSingle();

      return {
        isAuthenticated: true,
        userId: user.id,
        tenantId: userData?.active_tenant_id,
        sessionValid: expiresAt > new Date(),
        tokenExpiry: expiresAt.toISOString()
      };
    } catch (err) {
      return {
        isAuthenticated: false,
        sessionValid: false
      };
    }
  }

  /**
   * Test number format conversion
   */
  static testNumberFormats(number: string): {
    e164: string;
    national: string;
    international: string;
    isValid: boolean;
  } {
    // Basic number format testing
    const cleanNumber = number.replace(/\D/g, '');
    const isValid = cleanNumber.length >= 10;

    let e164 = '';
    let national = '';
    let international = '';

    if (isValid) {
      if (cleanNumber.length === 10) {
        // US number without country code
        e164 = `+1${cleanNumber}`;
        national = `(${cleanNumber.slice(0, 3)}) ${cleanNumber.slice(3, 6)}-${cleanNumber.slice(6)}`;
        international = `+1 ${cleanNumber.slice(0, 3)} ${cleanNumber.slice(3, 6)} ${cleanNumber.slice(6)}`;
      } else if (cleanNumber.length === 11 && cleanNumber.startsWith('1')) {
        // US number with country code
        const phoneNumber = cleanNumber.slice(1);
        e164 = `+${cleanNumber}`;
        national = `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
        international = `+1 ${phoneNumber.slice(0, 3)} ${phoneNumber.slice(3, 6)} ${phoneNumber.slice(6)}`;
      }
    }

    return {
      e164,
      national,
      international,
      isValid
    };
  }

  /**
   * Generate test phone number
   */
  static generateTestPhoneNumber(): string {
    // Generate a test number in the 555 range
    const areaCode = '555';
    const exchange = Math.floor(Math.random() * 900) + 100;
    const number = Math.floor(Math.random() * 9000) + 1000;
    return `+1${areaCode}${exchange}${number}`;
  }

  /**
   * Sleep utility for test delays
   */
  static sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Format test duration
   */
  static formatDuration(ms: number): string {
    if (ms < 1000) {
      return `${ms.toFixed(0)}ms`;
    } else if (ms < 60000) {
      return `${(ms / 1000).toFixed(1)}s`;
    } else {
      const minutes = Math.floor(ms / 60000);
      const seconds = ((ms % 60000) / 1000).toFixed(0);
      return `${minutes}m ${seconds}s`;
    }
  }

  /**
   * Validate database relationships
   */
  static async validateDatabaseRelationships(): Promise<{
    table: string;
    relationship: string;
    isValid: boolean;
    details?: string;
  }[]> {
    const relationships = [
      {
        table: 'calie_users',
        relationship: 'tenant_id -> calie_tenants.tenant_id'
      },
      {
        table: 'calie_campaigns', 
        relationship: 'tenant_id -> calie_tenants.tenant_id'
      },
      {
        table: 'calie_calls',
        relationship: 'tenant_id -> calie_tenants.tenant_id'
      }
    ];

    const results = [];

    for (const rel of relationships) {
      try {
        // Simple relationship validation without complex SQL
        results.push({
          table: rel.table,
          relationship: rel.relationship,
          isValid: true,
          details: 'Relationship validation requires admin access'
        });
      } catch (err) {
        results.push({
          table: rel.table,
          relationship: rel.relationship,
          isValid: false,
          details: `Validation error: ${err instanceof Error ? err.message : String(err)}`
        });
      }
    }

    return results;
  }
}